/**
 * WidgetPreview - web管理アプリ用ウィジェットプレビューコンポーネント
 *
 * タスク詳細ページで showWidget の結果を読み取り専用で表示する。
 * desktop-app の WidgetInlineDisplay と異なり、sendPrompt は無効化される。
 */

import { Download } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

export interface WidgetPreviewProps {
  title: string;
  widgetCode: string;
  mode: 'svg' | 'html';
}

/** テーマCSS変数をiframe用に取得 */
function getThemeCSSVars(): string {
  const style = getComputedStyle(document.documentElement);
  const vars = [
    'background',
    'foreground',
    'card',
    'card-foreground',
    'primary',
    'primary-foreground',
    'secondary',
    'secondary-foreground',
    'muted',
    'muted-foreground',
    'accent',
    'accent-foreground',
    'destructive',
    'destructive-foreground',
    'border',
    'input',
    'ring',
  ];
  return vars
    .map((name) => {
      const value = style.getPropertyValue(`--${name}`).trim();
      return value ? `--${name}: ${value};` : '';
    })
    .filter(Boolean)
    .join('\n  ');
}

/** 共通スクリプト（高さ通知・テーマ更新・エラーハンドリング） */
function commonScripts(bodyStyle: string): string {
  return `
    // sendPrompt は管理画面では無効化（呼び出しを無視）
    function sendPrompt() {}
    // テーマ動的更新リスナー
    window.addEventListener('message', function(e) {
      if (e.data && e.data.type === 'updateTheme' && e.data.css) {
        var el = document.getElementById('theme-vars');
        if (el) el.textContent = ':root {' + e.data.css + '} * { box-sizing: border-box; } ${bodyStyle}';
      }
    });
    // 高さ通知
    // - body.scrollHeight でビューポート非依存に計測（documentElement.scrollHeight は常に
    //   ビューポート高以上を返すため、iframe を拡大するとさらに増大してフィードバックループになる）
    // - requestAnimationFrame でデバウンス（ResizeObserver が 1 フレーム内に複数回発火しても 1 回送信）
    // - 直前と 2px 未満の差分は無視（サブピクセル丸めによる微振動を抑止）
    var _lastReportedHeight = -1;
    var _rafScheduled = false;
    function scheduleNotify() {
      if (_rafScheduled) return;
      _rafScheduled = true;
      requestAnimationFrame(function() {
        _rafScheduled = false;
        if (!document.body) return;
        var h = Math.ceil(document.body.scrollHeight);
        if (h <= 0) return;
        if (Math.abs(h - _lastReportedHeight) < 2) return;
        _lastReportedHeight = h;
        window.parent.postMessage({ type: 'resize', height: h }, '*');
      });
    }
    var _ro = new ResizeObserver(scheduleNotify);
    if (document.body) { _ro.observe(document.body); scheduleNotify(); }
    else { document.addEventListener('DOMContentLoaded', function() { _ro.observe(document.body); scheduleNotify(); }); }
    // エラーハンドリング
    window.onerror = function(message, source, lineno, colno, error) {
      window.parent.postMessage({
        type: 'error',
        message: String(message),
        source: source,
        lineno: lineno
      }, '*');
    };`;
}

// html 側に overflow: hidden を付与し、iframe 自身のスクロールバー出現に伴う
// body 幅の変動（再リフロー）を抑止する。
const HTML_BODY_STYLE =
  'html { overflow: hidden; } body { margin: 0; padding: 8px; font-family: system-ui, -apple-system, sans-serif; background: hsl(var(--background)); color: hsl(var(--foreground)); overflow: hidden; }';

const SVG_BODY_STYLE =
  'html { overflow: hidden; } body { margin: 0; padding: 0; background: transparent; overflow: hidden; display: flex; align-items: center; justify-content: center; } svg { display: block; width: 100%; height: auto; }';

/** HTML モード用 iframe テンプレート */
function buildHtmlIframeHTML(widgetCode: string, themeCSSVars: string): string {
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta http-equiv="Content-Security-Policy"
    content="default-src 'none'; style-src 'unsafe-inline'; script-src 'unsafe-inline';">
  <style id="theme-vars">
    :root { ${themeCSSVars} }
    * { box-sizing: border-box; }
    ${HTML_BODY_STYLE}
  </style>
  <script>${commonScripts(HTML_BODY_STYLE)}</script>
</head>
<body>${widgetCode}</body>
</html>`;
}

/** SVG モード用 iframe テンプレート */
function buildSvgIframeHTML(widgetCode: string, themeCSSVars: string): string {
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta http-equiv="Content-Security-Policy"
    content="default-src 'none'; style-src 'unsafe-inline'; script-src 'unsafe-inline';">
  <style id="theme-vars">
    :root { ${themeCSSVars} }
    * { box-sizing: border-box; }
    ${SVG_BODY_STYLE}
  </style>
  <script>${commonScripts(SVG_BODY_STYLE)}</script>
</head>
<body>${widgetCode}</body>
</html>`;
}

/** iframe 高さの上限を算出（ウィジェット内部で 100vh 等を使った場合の無限ループ対策） */
function computeMaxIframeHeight(): number {
  if (typeof window === 'undefined') return 1200;
  return Math.max(400, Math.min(window.innerHeight * 0.8, 1600));
}

export function WidgetPreview({ title, widgetCode, mode }: WidgetPreviewProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const { resolvedTheme } = useTheme();

  // biome-ignore lint/correctness/useExhaustiveDependencies: resolvedTheme はテーマ変更検知のトリガー
  const themeCSSVars = useMemo(() => getThemeCSSVars(), [resolvedTheme]);

  const srcDoc = useMemo(
    () =>
      mode === 'svg'
        ? buildSvgIframeHTML(widgetCode, themeCSSVars)
        : buildHtmlIframeHTML(widgetCode, themeCSSVars),
    [widgetCode, themeCSSVars, mode],
  );

  // postMessage リスナー（resize と error のみ処理）
  const handleMessage = useCallback((event: MessageEvent) => {
    if (event.source !== iframeRef.current?.contentWindow) return;
    if (!event.data || typeof event.data.type !== 'string') return;

    switch (event.data.type) {
      case 'resize':
        if (typeof event.data.height === 'number' && event.data.height > 0) {
          setIframeHeight(Math.min(event.data.height + 16, computeMaxIframeHeight()));
        }
        break;
      case 'error':
        setError(String(event.data.message));
        break;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  // テーマ変更時に iframe に通知
  // biome-ignore lint/correctness/useExhaustiveDependencies: resolvedTheme はテーマ変更検知のトリガー
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    const css = getThemeCSSVars();
    iframe.contentWindow.postMessage({ type: 'updateTheme', css }, '*');
  }, [resolvedTheme]);

  // ダウンロードハンドラー
  const handleDownload = useCallback(() => {
    const extension = mode === 'svg' ? '.svg' : '.html';
    const mimeType = mode === 'svg' ? 'image/svg+xml' : 'text/html';
    const blob = new Blob([widgetCode], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title}${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }, [title, widgetCode, mode]);

  return (
    <div className="my-2">
      {/* ヘッダー: タイトル + ダウンロードボタン */}
      <div className="flex items-center justify-between rounded-t-md border border-b-0 border-border bg-muted/30 px-3 py-1.5">
        <span className="text-xs font-medium text-muted-foreground">{title}</span>
        <button
          type="button"
          onClick={handleDownload}
          className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          title={`${title}.${mode === 'svg' ? 'svg' : 'html'} をダウンロード`}
        >
          <Download className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* エラー表示 */}
      {error && (
        <div className="border border-b-0 border-border bg-red-950/30 px-3 py-2 text-xs text-red-400">
          ウィジェットエラー: {error}
        </div>
      )}

      {/* サンドボックス iframe */}
      <iframe
        ref={iframeRef}
        srcDoc={srcDoc}
        sandbox="allow-scripts"
        title={title}
        className="w-full rounded-b-md border border-border"
        style={{ height: iframeHeight }}
      />
    </div>
  );
}
